import React from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import InputPhoneSADC from "@/components/auth/InputPhoneSADC.jsx";
import Select from "@/components/ui/Select.jsx";
import PharmacyDesktopHeader from "@/components/nav/PharmacyDesktopHeader.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import TopBar from "@/sot/primitives/TopBar.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import {
  createPharmacyInviteDraft,
  createPharmacyInviteSignInUrl,
  getPharmacyRolePermissionCopy,
  PHARMACY_EXPAND_MORE_ICON,
  PHARMACY_MEMBER_FLASH_KEY,
  PHARMACY_MEMBER_ROLE_OPTIONS,
  persistPharmacyStaffInvite,
  readPharmacyNotificationsForActor,
  readPharmacyProfileForActor,
  savePharmacyProfile,
  sendPharmacyInviteEmail,
  upsertPharmacyStaffMember,
  PHARMACY_NOTIFICATIONS_CHANGED_EVENT,
  PHARMACY_NOTIFICATIONS_STORAGE_KEY,
  PHARMACY_PROFILE_CHANGED_EVENT,
  PHARMACY_PROFILE_STORAGE_KEY,
} from "@/services/pharmacyLaneService.js";

const MUTED_TEXT = "text-[rgb(var(--clr-on-surface-variant))]";
const PRIMARY_TEXT = "text-[rgb(var(--clr-on-surface))]";

function splitPhoneParts(value = "") {
  const text = String(value || "").trim();
  if (!text.startsWith("+")) {
    return { cc: "+27", digits: text.replace(/\D/g, "") };
  }

  const digits = text.replace(/[^\d]/g, "");
  const supported = ["267", "27", "260", "263", "264", "265", "266", "258"];
  const match = supported.find((code) => digits.startsWith(code)) || "27";
  return {
    cc: `+${match}`,
    digits: digits.slice(match.length),
  };
}

function createEmptyMember() {
  return {
    fullName: "",
    email: "",
    phoneDigits: "",
    phoneCc: "+27",
    roleValue: "branch-admin",
  };
}

function pickFirstNonEmpty(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function buildPharmacyInviteIdentity(profile = {}) {
  return {
    pharmacyLogoUrl: pickFirstNonEmpty(
      profile?.pharmacyLogoUrl,
      profile?.logoUrl,
      profile?.logo,
      profile?.brandingLogoUrl,
      profile?.brandLogoUrl,
      profile?.avatarUrl,
      profile?.imageUrl,
    ),
    pharmacyAddressLine1: pickFirstNonEmpty(
      profile?.addressLine1,
      profile?.address,
      profile?.streetAddress,
      profile?.street_address,
      profile?.line1,
    ),
    pharmacyAddressLine2: pickFirstNonEmpty(
      profile?.addressLine2,
      profile?.address2,
      profile?.streetAddress2,
      profile?.street_address_2,
      profile?.line2,
    ),
    pharmacyCityRegionPostal: [
      pickFirstNonEmpty(profile?.city),
      pickFirstNonEmpty(profile?.region, profile?.state, profile?.province),
      pickFirstNonEmpty(profile?.postalCode, profile?.postal_code, profile?.postcode, profile?.zip),
    ]
      .filter(Boolean)
      .join(", ")
      .replace(/,\s*,/g, ", ")
      .trim(),
    pharmacyCountry: pickFirstNonEmpty(profile?.country),
  };
}

function getLocalizedRoleOptions(t) {
  return PHARMACY_MEMBER_ROLE_OPTIONS.filter((option) => option.value !== "owner").map((option) => {
    const permissionCopy = getPharmacyRolePermissionCopy(option.value);
    if (option.value === "dispensing-pharmacist") {
      return {
        ...option,
        label: t("pharmacy.settings.members.roles.dispensing.label", "Dispensing pharmacist"),
        note: t(
          "pharmacy.settings.members.roles.dispensing.note",
          permissionCopy.summary,
        ),
        permissionDetail: t("pharmacy.settings.members.roles.dispensing.detail", permissionCopy.detail),
      };
    }

    if (option.value === "inventory-manager") {
      return {
        ...option,
        label: t("pharmacy.settings.members.roles.inventory.label", "Inventory manager"),
        note: t(
          "pharmacy.settings.members.roles.inventory.note",
          permissionCopy.summary,
        ),
        permissionDetail: t("pharmacy.settings.members.roles.inventory.detail", permissionCopy.detail),
      };
    }

    if (option.value === "finance-lead") {
      return {
        ...option,
        label: t("pharmacy.settings.members.roles.finance.label", "Finance lead"),
        note: t(
          "pharmacy.settings.members.roles.finance.note",
          permissionCopy.summary,
        ),
        permissionDetail: t("pharmacy.settings.members.roles.finance.detail", permissionCopy.detail),
      };
    }

    if (option.value === "delivery-lead") {
      return {
        ...option,
        label: t("pharmacy.settings.members.roles.delivery.label", "Delivery lead"),
        note: t(
          "pharmacy.settings.members.roles.delivery.note",
          permissionCopy.summary,
        ),
        permissionDetail: t("pharmacy.settings.members.roles.delivery.detail", permissionCopy.detail),
      };
    }

    return {
      ...option,
      label: t("pharmacy.settings.members.roles.branchAdmin.label", "Branch admin"),
      note: t(
        "pharmacy.settings.members.roles.branchAdmin.note",
        permissionCopy.summary,
      ),
      permissionDetail: t("pharmacy.settings.members.roles.branchAdmin.detail", permissionCopy.detail),
    };
  });
}

export default function PharmacyMemberCreatePage() {
  const navigate = useNavigate();
  const auth = useAuth() || {};
  const { t } = useLanguage();
  const ownerUserId = String(auth?.user?.id || "").trim();
  const actor = { userId: ownerUserId, email: auth?.user?.email };
  const [profile, setProfile] = React.useState(() => readPharmacyProfileForActor(actor));
  const [notifications, setNotifications] = React.useState(() => readPharmacyNotificationsForActor(actor));
  const [values, setValues] = React.useState(() => {
    const base = createEmptyMember();
    const next = splitPhoneParts("");
    return { ...base, phoneCc: next.cc, phoneDigits: next.digits };
  });
  const [saving, setSaving] = React.useState(false);
  const [submitError, setSubmitError] = React.useState("");

  React.useEffect(() => {
    const refreshProfile = () => setProfile(readPharmacyProfileForActor(actor));
    const refreshNotifications = () => setNotifications(readPharmacyNotificationsForActor(actor));
    refreshProfile();
    refreshNotifications();
    const handleStorage = (event) => {
      if (!event?.key || event.key === PHARMACY_PROFILE_STORAGE_KEY) refreshProfile();
      if (!event?.key || event.key === PHARMACY_NOTIFICATIONS_STORAGE_KEY) refreshNotifications();
    };

    window.addEventListener(PHARMACY_PROFILE_CHANGED_EVENT, refreshProfile);
    window.addEventListener(PHARMACY_NOTIFICATIONS_CHANGED_EVENT, refreshNotifications);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(PHARMACY_PROFILE_CHANGED_EVENT, refreshProfile);
      window.removeEventListener(PHARMACY_NOTIFICATIONS_CHANGED_EVENT, refreshNotifications);
      window.removeEventListener("storage", handleStorage);
    };
  }, [actor.userId, actor.email]);

  const roleOptions = React.useMemo(() => getLocalizedRoleOptions(t), [t]);
  const selectedRole =
    roleOptions.find((option) => option.value === values.roleValue) || roleOptions[0];
  const canSave =
    String(values.fullName || "").trim() &&
    String(values.email || "").trim() &&
    String(values.phoneDigits || "").trim() &&
    !saving;

  async function handleSubmit(event) {
    event.preventDefault();
    if (!canSave) return;

    setSaving(true);
    setSubmitError("");
    try {
      const draftMember = createPharmacyInviteDraft({
        fullName: values.fullName,
        email: values.email,
        phone: `${values.phoneCc}${values.phoneDigits}`,
        roleValue: values.roleValue,
      });
      const inviteLink =
        draftMember.inviteLink || createPharmacyInviteSignInUrl(values.email, "/pharmacy/dashboard");

      await sendPharmacyInviteEmail({
        email: values.email,
        fullName: values.fullName,
        roleValue: values.roleValue,
        pharmacyName: profile?.pharmacyName || profile?.tradingName || "",
        inviterName: profile?.adminName || "",
        inviteLink,
        ...buildPharmacyInviteIdentity(profile),
      });

      let memberRecord = {
        ...draftMember,
        inviteLink,
      };

      try {
        const persistedMember = await persistPharmacyStaffInvite({
          profile,
          member: memberRecord,
          ownerUserId,
        });
        memberRecord = {
          ...memberRecord,
          ...persistedMember,
        };
      } catch (persistError) {
        console.warn(
          "[PharmacyMemberCreatePage] invite persistence fallback:",
          persistError?.message || persistError,
        );
      }

      const nextProfile = upsertPharmacyStaffMember(profile, memberRecord);
      savePharmacyProfile(nextProfile);
      sessionStorage.setItem(
        PHARMACY_MEMBER_FLASH_KEY,
        t(
          "pharmacy.settings.members.create.inviteSent",
          "Invitation email sent. The member stays pending until they sign in with the invited email.",
        ),
      );
      navigate("/pharmacy/settings", { replace: true });
    } catch (error) {
      console.warn("[PharmacyMemberCreatePage] invite submit failed:", error?.message || error);
      setSubmitError(
        error?.message
          ? t(
              "pharmacy.settings.members.create.inviteFailedDetail",
              "Invite email failed: {message}",
              { message: error.message },
            )
          : t(
              "pharmacy.settings.members.create.inviteFailed",
              "Invite email could not be delivered right now. Please try again.",
            ),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-dvh bg-[rgb(var(--clr-bg))] text-[rgb(var(--clr-on-surface))]">
      <PharmacyDesktopHeader section="settings" notifications={notifications} />

      <main className="afrimed-rail afrimed-rail--comfortable pt-3 pb-10">
        <TopBar
          title={t("pharmacy.settings.members.create.title", "New member")}
          subtitle={t(
            "pharmacy.settings.members.create.subtitle",
            "Add a branch team member for dispensing, stock, finance, or delivery handoff.",
          )}
          onBack={() => navigate("/pharmacy/settings")}
          titleWrapperClassName="mt-2 mb-5"
        />

        <div className="max-w-[1160px] space-y-6">
          <div className="max-w-[760px]">
            <p className={`text-body-medium ${MUTED_TEXT}`}>
              {t(
                "pharmacy.settings.members.create.copy",
                "The principal owner or admin stays as the branch permission anchor. Use this step to add the day-to-day pharmacy team cleanly so staff access stays consistent across Pharmacy, Admin, and Medical Institute lanes.",
              )}
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <section>
              <h2 className={`text-title-large ${PRIMARY_TEXT}`}>
                {t("pharmacy.settings.members.create.roleGuideTitle", "Role guide")}
              </h2>
              <p className={`mt-1 text-body-medium ${MUTED_TEXT}`}>
                {t(
                  "pharmacy.settings.members.create.roleGuideCopy",
                  "Mirror the same clean handoff logic across Pharmacy, Admin, and Medical Institutes. Assign only the branch role this person needs right now.",
                )}
              </p>

              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                {roleOptions.map((option) => (
                  <Card key={option.value} className="p-4">
                    <div className={`text-body-large ${PRIMARY_TEXT}`}>{option.label}</div>
                    <p className={`mt-1 text-body-small ${MUTED_TEXT}`}>{option.note}</p>
                    <p className={`mt-2 text-body-small ${MUTED_TEXT}`}>{option.permissionDetail}</p>
                  </Card>
                ))}
              </div>
            </section>

            <section className="space-y-6">
              {submitError ? (
                <div className="rounded-[12px] bg-[rgba(220,38,38,0.08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">
                  {submitError}
                </div>
              ) : null}

              <div>
                <h2 className={`text-headline-small ${PRIMARY_TEXT}`}>
                  {t("pharmacy.settings.members.create.formTitle", "Member details")}
                </h2>
                <p className={`mt-1 text-body-medium ${MUTED_TEXT}`}>
                  {t(
                    "pharmacy.settings.members.create.formCopy",
                    "Keep this invite lightweight now. Roles can still be adjusted later from Settings.",
                  )}
                </p>
              </div>

              <TextField56
                tone="surface"
                label={t("pharmacy.settings.members.form.name", "Full name")}
                value={values.fullName}
                onChange={(next) => setValues((current) => ({ ...current, fullName: next }))}
              />

              <TextField56
                tone="surface"
                label={t("pharmacy.settings.members.form.email", "Email")}
                type="email"
                value={values.email}
                onChange={(next) => setValues((current) => ({ ...current, email: next }))}
              />

              <div>
                <label className={`mb-2 block text-label-medium ${MUTED_TEXT}`}>
                  {t("pharmacy.settings.members.form.phone", "Phone")}
                </label>
                <InputPhoneSADC
                  tone="surface"
                  value={values.phoneDigits}
                  cc={values.phoneCc}
                  onChange={(next) => setValues((current) => ({ ...current, phoneDigits: next }))}
                  onChangeCc={(next) => setValues((current) => ({ ...current, phoneCc: next }))}
                />
              </div>

              <div>
                <Select
                  id="pharmacy-member-role"
                  label={t("pharmacy.settings.members.form.role", "Role")}
                  value={values.roleValue}
                  onValueChange={(nextValue) =>
                    setValues((current) => ({ ...current, roleValue: nextValue }))
                  }
                  options={roleOptions}
                  indicatorSrc={PHARMACY_EXPAND_MORE_ICON}
                  triggerClassName="h-[56px] rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] text-body-large text-[rgb(var(--clr-on-surface))]"
                  contentClassName="rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.10),0px_6px_12px_-4px_rgba(26,27,35,0.08)]"
                  optionClassName="py-3 text-body-large text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.04)]"
                />
                <p className={`mt-2 text-body-small ${MUTED_TEXT}`}>{selectedRole?.note}</p>
                {selectedRole?.permissionDetail ? (
                  <div className="mt-3 rounded-[12px] bg-[rgba(var(--afrimed-primary),0.08)] px-4 py-3">
                    <div className={`text-label-medium ${PRIMARY_TEXT}`}>
                      {t("pharmacy.settings.members.create.selectedRoleTitle", "Selected role permissions")}
                    </div>
                    <p className={`mt-1 text-body-small ${MUTED_TEXT}`}>
                      {selectedRole.permissionDetail}
                    </p>
                  </div>
                ) : null}
              </div>
            </section>

            <div className="flex flex-wrap items-center justify-start gap-2">
              <Button type="submit" disabled={!canSave}>
                {saving
                  ? t("pharmacy.settings.members.create.saving", "Sending invite...")
                  : t("pharmacy.settings.members.sheet.send", "Send invite")}
              </Button>
              <Button
                type="button"
                variant="text"
                onClick={() => navigate("/pharmacy/settings")}
              >
                {t("common.cancel", "Cancel")}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
